/**
 * OrejaRecordingPhase - Recording/input phase of the Oreja panel.
 *
 * Provides a project selector dropdown, voice/text input toggle, and a "Send Update"
 * button. Used for both the initial recording and refinement input.
 *
 * @module OrejaRecordingPhase
 */

import { useCallback, useEffect, useRef } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Lock } from 'lucide-react';
import { OrejaVoiceTextInput, type OrejaInputPayload } from './OrejaVoiceTextInput';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A project option for the selector dropdown. */
export interface OrejaProjectOption {
  clientId: string;
  projectId: string;
  listId: string;
  label: string;
}

export interface OrejaRecordingPhaseProps {
  /** Callback when the user submits their input. */
  onSend: (input: { type: 'voice'; blob: Blob } | { type: 'text'; text: string }) => void;
  /** Callback when a project is selected. */
  onProjectSelect: (clientId: string, projectId: string, listId: string) => void;
  /** Currently selected project. */
  selectedProject?: OrejaProjectOption | null;
  /** Available projects to choose from. */
  projects?: OrejaProjectOption[];
  /** Whether the parent is processing the input. */
  isLoading?: boolean;
  /** Optional prompt text override (e.g., for refinement mode). */
  promptText?: string;
  /** Whether the project selector is locked (e.g., during refinement after session starts). */
  projectLocked?: boolean;
}

/** localStorage key for persisting the last selected project. */
const STORAGE_KEY = 'oreja_last_project';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Recording/input phase: project selector, voice/text input, and send button.
 *
 * Voice recordings auto-send when the user stops recording. Text input uses
 * an explicit Send button (or Ctrl/Cmd+Enter).
 *
 * @param props - Component props
 * @returns The recording phase UI
 */
export function OrejaRecordingPhase({
  onSend,
  onProjectSelect,
  selectedProject,
  projects = [],
  isLoading = false,
  promptText,
  projectLocked = false,
}: OrejaRecordingPhaseProps) {
  // Use ref so the onInput callback always sees the latest selectedProject
  const selectedProjectRef = useRef(selectedProject);
  selectedProjectRef.current = selectedProject;

  // Restore last selected project from localStorage on mount
  useEffect(() => {
    if (selectedProject || projects.length === 0) return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw) as { listId?: string };
      if (!saved.listId) return;
      const match = projects.find((p) => p.listId === saved.listId);
      if (match) {
        onProjectSelect(match.clientId, match.projectId, match.listId);
      }
    } catch {
      // Corrupted data – ignore
    }
    // Only run on mount / when projects load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  // Voice: auto-send immediately. Text: handled by handleSendText below.
  const handleInput = useCallback((payload: OrejaInputPayload) => {
    if (payload.type === 'voice' && selectedProjectRef.current) {
      onSend(payload);
    }
  }, [onSend]);

  const handleProjectChange = useCallback(
    (value: string) => {
      const project = projects.find(
        (p) => `${p.clientId}::${p.projectId}::${p.listId}` === value,
      );
      if (project) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ listId: project.listId }));
        onProjectSelect(project.clientId, project.projectId, project.listId);
      }
    },
    [projects, onProjectSelect],
  );

  const selectedKey = selectedProject
    ? `${selectedProject.clientId}::${selectedProject.projectId}::${selectedProject.listId}`
    : undefined;

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Prompt text */}
      {promptText && (
        <p className="text-sm text-muted-foreground">{promptText}</p>
      )}

      {/* Project selector */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-1.5">
          <label className="text-sm font-medium" htmlFor="oreja-project-select">
            Project
          </label>
          {projectLocked && (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid="project-locked-indicator">
              <Lock className="h-3 w-3" />
              locked
            </span>
          )}
        </div>
        <Select value={selectedKey} onValueChange={handleProjectChange} disabled={projectLocked}>
          <SelectTrigger id="oreja-project-select">
            <SelectValue placeholder="Select a project..." />
          </SelectTrigger>
          <SelectContent>
            {projects.map((p) => {
              const key = `${p.clientId}::${p.projectId}::${p.listId}`;
              return (
                <SelectItem key={key} value={key}>
                  {p.label}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {/* No project selected hint */}
      {!selectedProject && (
        <p className="text-xs text-muted-foreground">Select a project to start recording</p>
      )}

      {/* Voice/Text input */}
      <OrejaVoiceTextInput
        onInput={handleInput}
        onTextSubmit={(text) => onSend({ type: 'text', text })}
        placeholder="Type your update..."
        disabled={isLoading || !selectedProject}
      />

      {/* Loading indicator after submission */}
      {isLoading && (
        <div className="flex items-center justify-center gap-2 py-3 text-sm text-muted-foreground bg-muted/30 rounded-md">
          <Loader2 className="h-4 w-4 animate-spin" />
          Starting session...
        </div>
      )}
    </div>
  );
}
